import { useState } from 'react'
import { Button, Divider, IconButton, Menu, MenuItem, Tooltip } from '@mui/material'
import TableChartIcon from '@mui/icons-material/TableChart'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import type { Editor } from '@tiptap/react'
import { TableSizePicker, type TTableSize } from './TableSizePicker'

type TTableAction = {
  label: string
  run: (editor: Editor) => boolean
}

const TABLE_ACTION_GROUPS: TTableAction[][] = [
  [
    { label: 'Insert row above', run: editor => editor.chain().focus().addRowBefore().run() },
    { label: 'Insert row below', run: editor => editor.chain().focus().addRowAfter().run() },
    { label: 'Insert column left', run: editor => editor.chain().focus().addColumnBefore().run() },
    { label: 'Insert column right', run: editor => editor.chain().focus().addColumnAfter().run() },
  ],
  [
    { label: 'Delete row', run: editor => editor.chain().focus().deleteRow().run() },
    { label: 'Delete column', run: editor => editor.chain().focus().deleteColumn().run() },
  ],
  [
    { label: 'Header row on / off', run: editor => editor.chain().focus().toggleHeaderRow().run() },
    { label: 'Delete table', run: editor => editor.chain().focus().deleteTable().run() },
  ],
]

export type TTableControlsProps = {
  editor: Editor
  isInTable: boolean
  disabled: boolean
}

export const TableControls = ({ editor, isInTable, disabled }: TTableControlsProps) => {
  const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)

  const insertTable = ({ rows, cols }: TTableSize) => {
    setPickerAnchor(null)
    editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run()
  }

  const runAction = (action: TTableAction) => {
    setMenuAnchor(null)
    action.run(editor)
  }

  if (isInTable) {
    return (
      <>
        <Button
          size="small"
          color="inherit"
          disabled={disabled}
          startIcon={<TableChartIcon fontSize="small" />}
          endIcon={<ArrowDropDownIcon />}
          onClick={event => setMenuAnchor(event.currentTarget)}
          sx={{ minWidth: 0, px: 1, textTransform: 'none' }}
        >
          Table
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          {TABLE_ACTION_GROUPS.flatMap((group, index) => [
            ...(index > 0 ? [<Divider key={`divider-${group[0].label}`} />] : []),
            ...group.map(action => (
              <MenuItem key={action.label} dense onClick={() => runAction(action)}>
                {action.label}
              </MenuItem>
            )),
          ])}
        </Menu>
      </>
    )
  }

  return (
    <>
      <Tooltip title="Insert table">
        <IconButton
          size="small"
          disabled={disabled}
          onClick={event => setPickerAnchor(event.currentTarget)}
        >
          <TableChartIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <TableSizePicker
        anchorEl={pickerAnchor}
        onClose={() => setPickerAnchor(null)}
        onPick={insertTable}
      />
    </>
  )
}
